import React from "react";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { formatDpatSlugAsCodeLabel } from "utils/dpatIndicatorCmsHelpers";
import { useDpatCommentFeedback } from "context/DpatCommentFeedbackContext";
import { useDpatIndicatorSlug } from "context/DpatIndicatorSlugContext";
import {
  findDetailScoreRowForSlug,
  isDpatCompletedOrClosedStatus,
  resolveRoleStandingCiStatuses,
} from "utils/dpatManualScoreHelpers";

function formatCompactIndicatorCode(rawCodeOrSlug) {
  if (!rawCodeOrSlug) return "";

  const text = String(rawCodeOrSlug).trim().replace(/–|—/g, "-");

  const labeledRangeMatch = text.match(/^(CI|SDI|PI)\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/i);
  if (labeledRangeMatch) {
    return `${labeledRangeMatch[1].toUpperCase()} ${labeledRangeMatch[3]}`;
  }

  const slugRangeMatch = text.toLowerCase().match(/^(c|sdi|pi)(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
  if (slugRangeMatch) {
    const prefix = slugRangeMatch[1] === "c" ? "CI" : slugRangeMatch[1].toUpperCase();
    return `${prefix} ${slugRangeMatch[3]}`;
  }

  return text;
}

function resolveResultLabel({ indicatorCode, slug, isFinal }) {
  const rawCode = indicatorCode || (slug ? formatDpatSlugAsCodeLabel(slug) : "");
  const compactCode = formatCompactIndicatorCode(rawCode);
  if (!compactCode) return isFinal ? "CI Final Result" : "CI Result";
  return isFinal ? `${compactCode} Final Result` : `${compactCode} Result`;
}

export default function CiResultDisplay({ fulfillment, indicatorCode, slug }) {
  const { assessmentWorkflowStatus, scoreSheetSummary, assessmentStartTables } = useDpatCommentFeedback();
  const indicatorSlug = useDpatIndicatorSlug();
  const resolvedSlug = slug || indicatorSlug;
  const showFinal = isDpatCompletedOrClosedStatus(assessmentWorkflowStatus);

  let value = fulfillment || "N/A";
  let resultLabel = resolveResultLabel({ indicatorCode, slug: resolvedSlug, isFinal: false });

  if (showFinal && resolvedSlug) {
    const row = findDetailScoreRowForSlug(scoreSheetSummary, resolvedSlug, assessmentStartTables);
    if (row) {
      const standing = resolveRoleStandingCiStatuses(row);
      value = standing.finalFulfilled ? "Fulfilled" : "Not Fulfilled";
      resultLabel = resolveResultLabel({ indicatorCode, slug: resolvedSlug, isFinal: true });
    }
  }

  const isFulfilled = value === "Fulfilled";

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        marginTop: 16,
        marginLeft: 10,
        marginRight: 20,
        padding: "8px 14px",
        borderRadius: 8,
        border: `1.5px solid ${isFulfilled ? "#52c41a" : "#ff4d4f"}`,
        background: isFulfilled
          ? "linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)"
          : "linear-gradient(135deg, #fff2f0 0%, #ffccc7 100%)",
        boxShadow: `0 2px 8px ${isFulfilled ? "rgba(82, 196, 26, 0.15)" : "rgba(255, 77, 79, 0.15)"}`,
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: "#595959",
          textTransform: "uppercase",
        }}
      >
        {resultLabel}
      </span>
      {isFulfilled ? (
        <CheckCircleFilled style={{ fontSize: 16, color: "#52c41a" }} />
      ) : (
        <CloseCircleFilled style={{ fontSize: 16, color: "#ff4d4f" }} />
      )}
      <strong
        style={{
          fontSize: 14,
          color: isFulfilled ? "#389e0d" : "#cf1322",
          textTransform: "uppercase",
        }}
      >
        {value}
      </strong>
    </div>
  );
}
